import React, { useState, useEffect, useCallback } from 'react';
import { platformAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Shield, Database, Activity, Users, Eye, Download,
         AlertTriangle, RefreshCw, Archive, Wrench, CheckCircle2, XCircle,
         MessageCircleWarning, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const TABS = [
  { key: 'data', label: 'Data Health', icon: Database },
  { key: 'system', label: 'System Health', icon: Activity },
  { key: 'clients', label: 'Clients', icon: Users },
  { key: 'fixlog', label: 'Fix History', icon: Wrench },
  { key: 'issues', label: 'Issues', icon: MessageCircleWarning },
];

export default function PlatformAdminPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState('data');

  if (user?.role !== 'super_admin') {
    return (
      <div className="p-6">
        <div className="bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-lg p-6" data-testid="platform-access-denied">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
            <div>
              <h2 className="font-bold text-red-900 dark:text-red-100">Access Denied</h2>
              <p className="text-sm text-red-700 dark:text-red-300 mt-1">
                Platform Admin is restricted to Transsolution super-admin accounts.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="platform-admin-page">
      <div className="flex items-center gap-3">
        <Shield className="w-6 h-6 text-purple-600" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Platform Admin</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Transsolution platform operations — data integrity, system health, client access
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="flex gap-1">
          {TABS.map(t => {
            const Icon = t.icon;
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                data-testid={`platform-tab-${t.key}`}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                  active
                    ? 'border-purple-600 text-purple-700 dark:text-purple-300'
                    : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                <Icon className="w-4 h-4" />
                {t.label}
              </button>
            );
          })}
        </nav>
      </div>

      {tab === 'data' && <DataHealthTab />}
      {tab === 'system' && <SystemHealthTab />}
      {tab === 'clients' && <ClientsTab />}
      {tab === 'fixlog' && <FixHistoryTab />}
      {tab === 'issues' && <IssuesTab />}
    </div>
  );
}

/* ------------------------------------------------------------
 *  DATA HEALTH TAB
 * ------------------------------------------------------------ */
function DataHealthTab() {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [rows, setRows] = useState({});
  const [fixCategory, setFixCategory] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await platformAPI.dataHealthSummary();
      setSummary(res.data);
    } catch {
      toast.error('Failed to load data health');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const handler = (e) => setFixCategory(e.detail.category);
    window.addEventListener('open-fix-preview', handler);
    return () => window.removeEventListener('open-fix-preview', handler);
  }, []);

  const loadCategory = async (category) => {
    try {
      const res = await platformAPI.dataHealthCategory(category);
      setRows(prev => ({ ...prev, [category]: res.data.rows }));
      setExpanded(category);
    } catch {
      toast.error('Failed to load category details');
    }
  };

  const downloadBackup = async (category, format) => {
    try {
      const res = await platformAPI.dataHealthExport(category, format);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `data_health_${category}.${format}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`${format.toUpperCase()} backup downloaded`);
    } catch {
      toast.error('Backup download failed');
    }
  };

  return (
    <div className="space-y-4" data-testid="data-health-tab">
      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-600 dark:text-gray-400">
          Scans 7 categories of invisible/corrupt records.
          {summary && <span className="ml-2">Archived batches: <strong>{summary.archived_count}</strong></span>}
        </div>
        <button
          onClick={load}
          data-testid="data-health-rescan"
          className="flex items-center gap-2 px-3 py-1.5 text-sm bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50"
          disabled={loading}
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          {loading ? 'Scanning...' : 'Rescan'}
        </button>
      </div>

      <div className="grid gap-3">
        {summary?.categories?.map(cat => (
          <CategoryCard
            key={cat.category}
            cat={cat}
            expanded={expanded === cat.category}
            rows={rows[cat.category]}
            onExpand={() => loadCategory(cat.category)}
            onCollapse={() => setExpanded(null)}
            onBackup={downloadBackup}
            onArchived={load}
          />
        ))}
      </div>

      {fixCategory && (
        <FixPreviewModal
          category={fixCategory}
          onClose={() => setFixCategory(null)}
          onApplied={() => { setFixCategory(null); load(); }}
        />
      )}

      <LedgerRecalcCard />
      <WipeTestDataCard onWiped={load} />
    </div>
  );
}


/* ------------------------------------------------------------
 *  LEDGER RECALCULATION — dry-run + apply
 *  Reads from live + archived_records (hybrid safety)
 * ------------------------------------------------------------ */
function LedgerRecalcCard() {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);

  const dryRun = async () => {
    setLoading(true); setResult(null);
    try {
      const r = await platformAPI.ledgerRecalcDryRun();
      setResult(r.data);
    } catch {
      toast.error('Ledger recalc failed');
    } finally {
      setLoading(false);
    }
  };

  const apply = async () => {
    if (!result) return;
    if (!window.confirm(
      `Apply ${result.corrections_needed} ledger corrections?\n\n` +
      `Reads from live AND archived data. Non-destructive: only writes 'computed_balance' ` +
      `on existing party rows. Parties without a master record are reported but skipped.`
    )) return;
    setApplying(true);
    try {
      const r = await platformAPI.ledgerRecalcApply();
      toast.success(`Applied ${r.data.applied} corrections · op ${r.data.operation_id || 'none'}`);
      setResult(null);
    } catch {
      toast.error('Ledger apply failed');
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="bg-indigo-50 dark:bg-indigo-950/30 border border-indigo-200 dark:border-indigo-800 rounded-lg p-4" data-testid="ledger-recalc-card">
      <div className="flex items-start justify-between mb-3">
        <div>
          <h3 className="font-semibold text-indigo-900 dark:text-indigo-200 flex items-center gap-2">
            <Wrench className="w-4 h-4" /> Ledger Recalculation Engine
          </h3>
          <p className="text-xs text-indigo-700 dark:text-indigo-400 mt-1">
            Computes each party's true balance from invoices + receipts + advances across LIVE and ARCHIVED data.
            Non-destructive: only writes <code>computed_balance</code> on existing party rows.
          </p>
        </div>
        <button
          onClick={dryRun}
          disabled={loading}
          data-testid="ledger-recalc-dryrun"
          className="flex items-center gap-2 px-3 py-1.5 text-sm bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50"
        >
          <Eye className={`w-3.5 h-3.5 ${loading ? 'animate-pulse' : ''}`} />
          {loading ? 'Computing…' : 'Preview Changes'}
        </button>
      </div>

      {result && (
        <div className="mt-3 space-y-3">
          <div className="flex items-center gap-2 text-xs font-medium text-indigo-800 dark:text-indigo-200 bg-indigo-100 dark:bg-indigo-900/40 px-3 py-1.5 rounded">
            <Eye className="w-3.5 h-3.5" />
            PREVIEW MODE — no data has been changed yet. Review the diff below then click Apply to commit.
          </div>
          <div className="grid grid-cols-3 gap-3 text-xs">
            <div className="bg-white dark:bg-gray-800 rounded p-2">
              <div className="text-gray-500">Total Parties Scanned</div>
              <div className="text-lg font-bold">{result.total_parties}</div>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded p-2">
              <div className="text-gray-500">Corrections Needed</div>
              <div className={`text-lg font-bold ${result.corrections_needed > 0 ? 'text-amber-600' : 'text-emerald-600'}`}>
                {result.corrections_needed}
              </div>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded p-2">
              <div className="text-gray-500">In-Balance</div>
              <div className="text-lg font-bold text-emerald-600">
                {result.total_parties - result.corrections_needed}
              </div>
            </div>
          </div>

          {result.corrections_needed > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="text-gray-500 border-b border-indigo-200 dark:border-indigo-800">
                  <tr>
                    <th className="text-left p-1">Party</th>
                    <th className="text-right p-1">Invoices</th>
                    <th className="text-right p-1">Receipts</th>
                    <th className="text-right p-1">Advances</th>
                    <th className="text-right p-1">Stored</th>
                    <th className="text-right p-1">Computed</th>
                    <th className="text-right p-1">Diff</th>
                  </tr>
                </thead>
                <tbody>
                  {result.parties.filter(p => p.needs_correction).slice(0, 50).map((p, i) => (
                    <tr key={i} className="border-b border-indigo-100 dark:border-indigo-900/30">
                      <td className="p-1">
                        {p.party_name}
                        {!p.party_id && (
                          <span className="ml-1 text-[9px] px-1 bg-amber-200 text-amber-800 rounded">no master</span>
                        )}
                      </td>
                      <td className="p-1 text-right">{p.invoice_total.toLocaleString()}</td>
                      <td className="p-1 text-right">{p.receipt_total.toLocaleString()}</td>
                      <td className="p-1 text-right">{p.advance_balance.toLocaleString()}</td>
                      <td className="p-1 text-right text-gray-600">{p.stored_balance.toLocaleString()}</td>
                      <td className="p-1 text-right text-emerald-700 dark:text-emerald-400 font-medium">{p.computed_balance.toLocaleString()}</td>
                      <td className={`p-1 text-right font-bold ${Math.abs(p.diff) > 0.01 ? 'text-amber-600' : ''}`}>
                        {p.diff > 0 ? '+' : ''}{p.diff.toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="mt-3 flex justify-end">
                <button
                  onClick={apply}
                  disabled={applying}
                  data-testid="ledger-recalc-apply"
                  className="flex items-center gap-2 px-4 py-1.5 text-sm font-bold bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-50"
                >
                  <CheckCircle2 className="w-4 h-4" />
                  {applying ? 'Applying…' : `Apply ${result.corrections_needed} Corrections`}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------
 *  DANGER ZONE — Wipe Test Data
 *  Irreversible mass-delete of transactional + master collections.
 *  Requires typing an exact confirmation phrase before the button enables.
 * ------------------------------------------------------------ */
function WipeTestDataCard({ onWiped }) {
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [phrase, setPhrase] = useState('');
  const [loading, setLoading] = useState(false);
  const [wiping, setWiping] = useState(false);
  const [result, setResult] = useState(null);

  const openModal = async () => {
    setOpen(true);
    setPhrase('');
    setResult(null);
    setLoading(true);
    try {
      const r = await platformAPI.wipeTestDataPreview();
      setPreview(r.data);
    } catch {
      toast.error('Failed to load wipe preview');
      setOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const canSubmit = preview && phrase === preview.confirmation_phrase && !wiping;

  const doWipe = async () => {
    if (!canSubmit) return;
    setWiping(true);
    try {
      const r = await platformAPI.wipeTestData(phrase);
      setResult(r.data);
      toast.success(`Wiped ${r.data.total_deleted.toLocaleString('en-IN')} records`);
      if (onWiped) onWiped();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Wipe failed');
    } finally {
      setWiping(false);
    }
  };

  const close = () => {
    setOpen(false);
    setPhrase('');
    setPreview(null);
    setResult(null);
  };

  return (
    <>
      <div className="bg-rose-50 dark:bg-rose-950/30 border border-rose-300 dark:border-rose-800 rounded-lg p-4" data-testid="wipe-test-data-card">
        <div className="flex items-start justify-between">
          <div className="flex-1 pr-4">
            <h3 className="font-semibold text-rose-900 dark:text-rose-200 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" /> Danger Zone — Wipe All Test Data
            </h3>
            <p className="text-xs text-rose-800 dark:text-rose-300 mt-1">
              Permanently deletes all invoices, journals, ledgers, receipts, advances, parties,
              bank accounts, audit logs, TDS entries, issues, notifications and sequences.
              <strong className="ml-1">Cannot be undone.</strong> Users, settings, CMS and chart-of-accounts are preserved.
            </p>
          </div>
          <button
            onClick={openModal}
            data-testid="open-wipe-modal"
            className="flex items-center gap-2 px-3 py-1.5 text-sm bg-rose-600 text-white rounded hover:bg-rose-700 shrink-0"
          >
            <Trash2 className="w-3.5 h-3.5" />
            Wipe Test Data…
          </button>
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" data-testid="wipe-modal">
          <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-auto">
            <div className="border-b border-rose-200 dark:border-rose-800 p-4 flex items-center justify-between bg-rose-50 dark:bg-rose-950/40">
              <h3 className="font-semibold text-rose-900 dark:text-rose-200 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" />
                Wipe Test Data — Irreversible
              </h3>
              <button onClick={close} className="text-gray-400 hover:text-gray-600" data-testid="wipe-modal-close">
                <XCircle className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5 space-y-4">
              {loading && <p className="text-sm text-gray-500">Loading preview…</p>}

              {!loading && result && (
                <div className="space-y-3" data-testid="wipe-result">
                  <div className="flex items-center gap-2 text-sm font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-3 py-2">
                    <CheckCircle2 className="w-4 h-4" />
                    Wipe complete — {result.total_deleted.toLocaleString('en-IN')} records removed across {result.collections_wiped.length} collections.
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className="text-gray-500 border-b border-gray-200 dark:border-gray-700">
                        <tr>
                          <th className="text-left p-2">Collection</th>
                          <th className="text-right p-2">Deleted</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(result.deleted_per_collection).map(([coll, n]) => (
                          <tr key={coll} className="border-b border-gray-100 dark:border-gray-800">
                            <td className="p-2 font-mono text-gray-700 dark:text-gray-300">{coll}</td>
                            <td className="p-2 text-right font-mono">{n.toLocaleString('en-IN')}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {result.errors?.length > 0 && (
                    <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded px-3 py-2">
                      {result.errors.length} collection(s) errored — check backend logs.
                    </div>
                  )}
                  <button
                    onClick={close}
                    data-testid="wipe-modal-done"
                    className="w-full px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
                  >
                    Close
                  </button>
                </div>
              )}

              {!loading && preview && !result && (
                <>
                  <div className="text-sm text-rose-900 dark:text-rose-200 bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-800 rounded px-3 py-2">
                    This will permanently delete <strong>{preview.total.toLocaleString('en-IN')}</strong> records across <strong>{preview.collections.length}</strong> collections. This action cannot be undone.
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full text-xs" data-testid="wipe-preview-table">
                      <thead className="text-gray-500 border-b border-gray-200 dark:border-gray-700">
                        <tr>
                          <th className="text-left p-2">Collection</th>
                          <th className="text-right p-2">Rows to delete</th>
                        </tr>
                      </thead>
                      <tbody>
                        {preview.collections.map(coll => (
                          <tr key={coll} className="border-b border-gray-100 dark:border-gray-800">
                            <td className="p-2 font-mono text-gray-700 dark:text-gray-300">{coll}</td>
                            <td className={`p-2 text-right font-mono ${preview.counts[coll] > 0 ? 'text-rose-700 font-semibold' : 'text-gray-400'}`}>
                              {(preview.counts[coll] || 0).toLocaleString('en-IN')}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="space-y-2">
                    <label className="text-xs font-medium text-gray-700 dark:text-gray-300">
                      Type <code className="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-800 rounded font-mono text-rose-700">{preview.confirmation_phrase}</code> to confirm:
                    </label>
                    <input
                      type="text"
                      value={phrase}
                      onChange={e => setPhrase(e.target.value)}
                      placeholder={preview.confirmation_phrase}
                      autoComplete="off"
                      spellCheck="false"
                      data-testid="wipe-confirm-input"
                      className="w-full px-3 py-2 border-2 rounded text-sm font-mono focus:outline-none focus:ring-2 focus:ring-rose-500 border-rose-300 dark:border-rose-700 bg-white dark:bg-gray-800"
                    />
                  </div>

                  <div className="flex gap-3 pt-2">
                    <button
                      onClick={close}
                      disabled={wiping}
                      data-testid="wipe-cancel"
                      className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-800 text-gray-800 dark:text-gray-200 rounded hover:bg-gray-300 disabled:opacity-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={doWipe}
                      disabled={!canSubmit}
                      data-testid="wipe-confirm-button"
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-rose-600 text-white rounded hover:bg-rose-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                      {wiping ? 'Wiping…' : `Delete ${preview.total.toLocaleString('en-IN')} records`}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}


/* ------------------------------------------------------------
 *  FIX PREVIEW / APPLY MODAL — shows before/after diff
 * ------------------------------------------------------------ */
function FixPreviewModal({ category, onClose, onApplied }) {
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await platformAPI.fixPreview(category);
        setPlan(res.data);
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Fix preview failed');
        onClose();
      } finally {
        setLoading(false);
      }
    })();
  }, [category, onClose]);

  const apply = async () => {
    if (!window.confirm(
      `Apply ${plan.count} fixes for "${category}"?\n\n` +
      `A reversible operation_id will be created. All actions are audit-logged.`
    )) return;
    setApplying(true);
    try {
      const res = await platformAPI.fixApply(category);
      toast.success(`Applied ${res.data.applied}/${res.data.total_planned} fixes · op ${res.data.operation_id}`);
      onApplied();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fix apply failed');
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="fix-preview-modal">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-3xl w-full max-h-[85vh] flex flex-col">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div>
            <h3 className="font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <Wrench className="w-4 h-4 text-emerald-600" /> Auto-Fix Preview
            </h3>
            <code className="text-xs text-gray-500">{category}</code>
          </div>
          <button onClick={onClose} data-testid="fix-modal-close" className="text-gray-400 hover:text-gray-700"><XCircle className="w-5 h-5" /></button>
        </div>
        <div className="p-4 overflow-y-auto flex-1">
          {loading ? (
            <div className="text-sm text-gray-500">Building fix plan…</div>
          ) : !plan || plan.count === 0 ? (
            <div className="text-sm text-gray-500">No records need fixing.</div>
          ) : (
            <>
              <div className="text-sm text-gray-700 dark:text-gray-300 mb-3">
                <strong>{plan.count}</strong> record(s) will be modified.
                Each step shows the before → after diff. A reversible snapshot is
                captured <em>before</em> any change is applied.
              </div>
              <table className="w-full text-xs">
                <thead className="text-gray-500 border-b border-gray-200 dark:border-gray-700">
                  <tr>
                    <th className="text-left py-1">ID</th>
                    <th className="text-left py-1">Action</th>
                    <th className="text-left py-1">Before</th>
                    <th className="text-left py-1">After</th>
                  </tr>
                </thead>
                <tbody>
                  {plan.plan.slice(0, 200).map((step, i) => (
                    <tr key={i} className="border-b border-gray-100 dark:border-gray-800">
                      <td className="py-1 font-mono text-gray-700 dark:text-gray-300">
                        {step.advance_id || step.entry_id || step.receipt_id || step.invoice_id || '—'}
                      </td>
                      <td className="py-1">
                        <code className={`px-1 rounded text-[10px] ${step.action === 'flag_manual_review' ? 'bg-amber-100 text-amber-800' : 'bg-emerald-100 text-emerald-800'}`}>
                          {step.action}
                        </code>
                      </td>
                      <td className="py-1 text-gray-600">{formatDiff(step.before)}</td>
                      <td className="py-1 text-emerald-700 dark:text-emerald-400 font-medium">{formatDiff(step.after)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {plan.plan.length > 200 && (
                <div className="text-xs text-gray-500 mt-2">Showing first 200 of {plan.count}.</div>
              )}
            </>
          )}
        </div>
        {plan && plan.count > 0 && (
          <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
            <button onClick={onClose} className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700" data-testid="fix-modal-cancel">
              Cancel
            </button>
            <button
              onClick={apply}
              disabled={applying}
              data-testid="fix-modal-apply"
              className="flex items-center gap-2 px-4 py-1.5 text-sm font-bold bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-50"
            >
              <CheckCircle2 className="w-4 h-4" />
              {applying ? 'Applying…' : `Apply ${plan.count} Fixes`}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function formatDiff(obj) {
  if (!obj) return '—';
  return Object.entries(obj).map(([k, v]) => `${k}=${formatCell(v)}`).join(', ');
}

function CategoryCard({ cat, expanded, rows, onExpand, onCollapse, onBackup, onArchived }) {
  const { category, description, count, error } = cat;
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);

  const allIds = (rows || []).map(r => r[idFieldFor(category)]).filter(Boolean);

  const toggle = (id) => {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  };

  const toggleAll = () => {
    if (selected.size === allIds.length) setSelected(new Set());
    else setSelected(new Set(allIds));
  };

  const archiveSelected = async () => {
    if (selected.size === 0) return;
    const confirmWord = prompt(
      `You are about to archive ${selected.size} records in category "${category}".\n\n` +
      `These will be moved to archived_records (restorable within this Admin panel), and REMOVED from the live app.\n\n` +
      `Type the category name to confirm: ${category}`
    );
    if (confirmWord !== category) {
      toast.error('Category name mismatch — archive cancelled');
      return;
    }
    setBusy(true);
    try {
      const res = await platformAPI.archiveRecords({
        category,
        ids: Array.from(selected),
        confirm: category,
      });
      toast.success(`Archived ${res.data.archived} records (batch ${res.data.batch_id})`);
      setSelected(new Set());
      onArchived();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Archive failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden"
      data-testid={`data-health-card-${category}`}
    >
      <div className="p-4 flex items-start justify-between gap-3">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-gray-900 dark:text-white">{description}</h3>
            <span
              className={`px-2 py-0.5 text-xs font-bold rounded ${
                count > 0
                  ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
                  : 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
              }`}
            >
              {count < 0 ? 'ERR' : count}
            </span>
          </div>
          <code className="text-[11px] text-gray-500 dark:text-gray-500">{category}</code>
          {error && <div className="text-xs text-red-500 mt-1">{error}</div>}
        </div>
        <div className="flex gap-2 shrink-0">
          {count > 0 && (
            <>
              {CATEGORY_AUTOFIXABLE.has(category) && (
                <button
                  onClick={() => window.dispatchEvent(new CustomEvent('open-fix-preview', { detail: { category } }))}
                  data-testid={`fix-${category}`}
                  title="Preview + Apply Auto-Fix"
                  className="flex items-center gap-1 p-1.5 text-xs border border-emerald-300 dark:border-emerald-700 rounded text-emerald-700 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/20"
                >
                  <Wrench className="w-3.5 h-3.5" /> Fix
                </button>
              )}
              <button
                onClick={() => onBackup(category, 'csv')}
                data-testid={`backup-csv-${category}`}
                title="Download CSV backup"
                className="p-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                <Download className="w-3.5 h-3.5" /> CSV
              </button>
              <button
                onClick={() => onBackup(category, 'json')}
                data-testid={`backup-json-${category}`}
                className="p-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                <Download className="w-3.5 h-3.5" /> JSON
              </button>
              <button
                onClick={expanded ? onCollapse : onExpand}
                data-testid={`expand-${category}`}
                className="px-2 py-1 text-xs bg-purple-600 text-white rounded hover:bg-purple-700"
              >
                {expanded ? 'Hide' : 'View'}
              </button>
            </>
          )}
        </div>
      </div>

      {expanded && rows && (
        <div className="border-t border-gray-200 dark:border-gray-700 p-4 space-y-3">
          {rows.length === 0 ? (
            <div className="text-sm text-gray-500">No rows.</div>
          ) : (
            <>
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                  <input
                    type="checkbox"
                    checked={selected.size === allIds.length && allIds.length > 0}
                    onChange={toggleAll}
                    data-testid={`select-all-${category}`}
                  />
                  Select all ({selected.size} / {allIds.length})
                </label>
                {selected.size > 0 && CATEGORY_ARCHIVABLE.has(category) && (
                  <button
                    onClick={archiveSelected}
                    disabled={busy}
                    data-testid={`archive-selected-${category}`}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
                  >
                    <Archive className="w-3.5 h-3.5" />
                    {busy ? 'Archiving...' : `Archive ${selected.size}`}
                  </button>
                )}
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                    <tr>
                      <th className="text-left py-1 w-6"></th>
                      {Object.keys(rows[0] || {}).slice(0, 6).map(k => (
                        <th key={k} className="text-left py-1 px-2 font-medium">{k}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.slice(0, 50).map((r, i) => {
                      const rid = r[idFieldFor(category)];
                      return (
                        <tr key={i} className="border-b border-gray-100 dark:border-gray-800">
                          <td className="py-1">
                            {CATEGORY_ARCHIVABLE.has(category) && rid && (
                              <input
                                type="checkbox"
                                checked={selected.has(rid)}
                                onChange={() => toggle(rid)}
                                data-testid={`row-check-${rid}`}
                              />
                            )}
                          </td>
                          {Object.keys(rows[0] || {}).slice(0, 6).map(k => (
                            <td key={k} className="py-1 px-2 text-gray-700 dark:text-gray-300 truncate max-w-[200px]">
                              {formatCell(r[k])}
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {rows.length > 50 && (
                  <div className="text-xs text-gray-500 mt-2">Showing 50 of {rows.length}</div>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

const CATEGORY_ARCHIVABLE = new Set([
  'imbalanced_journals', 'journals_invalid_accounts', 'invoices_without_journal',
  'invoices_with_stale_journal',
  'orphan_receipts', 'advance_balance_mismatch', 'orphan_invoices_company',
]);

const CATEGORY_AUTOFIXABLE = new Set([
  'invoices_without_journal',
  'invoices_with_stale_journal',
  'imbalanced_journals',
  'advance_balance_mismatch',
  'orphan_receipts',
]);

function idFieldFor(category) {
  const map = {
    imbalanced_journals: 'entry_id',
    journals_invalid_accounts: 'entry_id',
    invoices_without_journal: 'invoice_id',
    invoices_with_stale_journal: 'invoice_id',
    orphan_receipts: 'receipt_id',
    advance_balance_mismatch: 'advance_id',
    orphan_invoices_company: 'invoice_id',
    duplicate_parties: 'normalized_name',
  };
  return map[category] || 'id';
}

function formatCell(v) {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

/* ------------------------------------------------------------
 *  SYSTEM HEALTH TAB
 * ------------------------------------------------------------ */
function SystemHealthTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await platformAPI.systemHealth();
      setData(r.data);
    } catch {
      toast.error('Failed to load system health');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); const id = setInterval(load, 30000); return () => clearInterval(id); }, [load]);

  if (!data) return <div className="text-gray-500 p-4">Loading…</div>;

  const { server, database, top_collections, clients, api_performance, auth_activity, transaction_trend, recent_errors } = data;

  return (
    <div className="space-y-5" data-testid="system-health-tab">
      <div className="flex items-center justify-between">
        <div className="text-xs text-gray-500">Auto-refresh every 30s · Last: {new Date(data.generated_at).toLocaleTimeString()}</div>
        <button
          onClick={load}
          className="flex items-center gap-2 px-3 py-1.5 text-sm bg-purple-600 text-white rounded hover:bg-purple-700"
          data-testid="system-health-refresh"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      {/* Server vitals */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat label="CPU" value={`${server.cpu_percent}%`} sub={`${server.cpu_count} cores`} warn={server.cpu_percent > 80} />
        <Stat label="RAM" value={`${server.ram_percent}%`} sub={`${server.ram_used_mb?.toLocaleString()} / ${server.ram_total_mb?.toLocaleString()} MB`} warn={server.ram_percent > 85} />
        <Stat label="Disk" value={`${server.disk_percent}%`} sub={`${server.disk_used_gb} / ${server.disk_total_gb} GB`} warn={server.disk_percent > 90} />
        <Stat label="Uptime" value={`${server.uptime_hours} h`} sub={server.platform?.split(' ')[0] || ''} />
      </div>

      {/* DB vitals */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat label="DB Objects" value={database.objects?.toLocaleString() || '—'} sub="All collections" />
        <Stat label="Data Size" value={`${database.data_size_mb} MB`} sub={`Storage: ${database.storage_size_mb} MB`} />
        <Stat label="Index Size" value={`${database.index_size_mb} MB`} />
        <Stat label="Collections" value={database.collections || '—'} />
      </div>

      {/* Top collections */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
        <h3 className="font-semibold mb-3 text-gray-900 dark:text-white">Top 5 Largest Collections</h3>
        <div className="space-y-1">
          {top_collections?.map(c => (
            <div key={c.name} className="flex items-center justify-between text-sm py-1">
              <code className="text-gray-700 dark:text-gray-300">{c.name}</code>
              <div className="flex gap-4 text-xs text-gray-500">
                <span>{c.count?.toLocaleString()} docs</span>
                <span>{c.size_kb} KB data</span>
                <span>{c.index_kb} KB idx</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* API perf */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
        <h3 className="font-semibold mb-3 text-gray-900 dark:text-white">API Performance (last 24h)</h3>
        {api_performance.total_requests_24h > 0 ? (
          <>
            <div className="grid grid-cols-4 gap-3 mb-3">
              <Stat label="Requests" value={api_performance.total_requests_24h?.toLocaleString()} />
              <Stat label="Avg Response" value={`${api_performance.avg_ms} ms`} />
              <Stat label="P95" value={`${api_performance.p95_ms} ms`} warn={api_performance.p95_ms > 1000} />
              <Stat label="Error Rate" value={`${api_performance.error_rate_pct}%`} warn={api_performance.error_rate_pct > 2} />
            </div>
            <div className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Top 10 Slowest Paths</div>
            <table className="w-full text-xs">
              <thead className="text-gray-500 border-b border-gray-200 dark:border-gray-700">
                <tr><th className="text-left py-1">Path</th><th className="text-right">Avg</th><th className="text-right">Max</th><th className="text-right">Count</th></tr>
              </thead>
              <tbody>
                {api_performance.slowest_paths?.map((p, i) => (
                  <tr key={i} className="border-b border-gray-100 dark:border-gray-800">
                    <td className="py-1"><code className="text-gray-700 dark:text-gray-300">{p.path}</code></td>
                    <td className="text-right">{p.avg_ms} ms</td>
                    <td className="text-right text-red-600">{p.max_ms} ms</td>
                    <td className="text-right">{p.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        ) : (
          <div className="text-sm text-gray-500">{api_performance.note || 'No data'}</div>
        )}
      </div>

      {/* Auth + Trend */}
      <div className="grid md:grid-cols-2 gap-3">
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <h3 className="font-semibold mb-3 text-gray-900 dark:text-white">Auth Activity (24h)</h3>
          {auth_activity.error ? (
            <div className="text-xs text-red-500">{auth_activity.error}</div>
          ) : (
            <>
              <div className="grid grid-cols-3 gap-2 mb-3">
                <Stat label="Logins" value={auth_activity.logins_24h} />
                <Stat label="Failed" value={auth_activity.failed_logins_24h} warn={auth_activity.failed_logins_24h > 5} />
                <Stat label="Logouts" value={auth_activity.logouts_24h} />
              </div>
              <div className="text-xs space-y-1 max-h-40 overflow-y-auto">
                {auth_activity.recent?.slice(0, 10).map((a, i) => (
                  <div key={i} className="flex justify-between text-gray-600 dark:text-gray-400">
                    <span>{a.user_name} — <code>{a.action}</code></span>
                    <span>{new Date(a.created_at).toLocaleTimeString()}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <h3 className="font-semibold mb-3 text-gray-900 dark:text-white">Transaction Volume (7 days)</h3>
          <div className="space-y-1">
            {transaction_trend?.map((d, i) => (
              <div key={i} className="flex items-center gap-2 text-xs">
                <span className="w-20 text-gray-500">{d.date}</span>
                <div className="flex-1 h-4 bg-gray-100 dark:bg-gray-900 rounded relative">
                  <div className="absolute left-0 top-0 h-full bg-purple-500 rounded-l" style={{ width: `${Math.min(100, (d.invoices || 0) * 10)}%` }} />
                </div>
                <span className="w-14 text-right text-gray-700 dark:text-gray-300">{d.invoices} inv</span>
                <span className="w-14 text-right text-gray-500">{d.receipts} rcpt</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Recent errors */}
      {recent_errors?.length > 0 && (
        <div className="bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <h3 className="font-semibold mb-2 text-red-900 dark:text-red-200 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" /> Recent Errors (last 50)
          </h3>
          <div className="text-xs space-y-1 max-h-40 overflow-y-auto">
            {recent_errors.slice(0, 20).map((e, i) => (
              <div key={i} className="flex items-center gap-2 text-red-700 dark:text-red-300">
                <span className="font-mono font-bold">{e.status}</span>
                <span className="font-mono">{e.method}</span>
                <code className="flex-1 truncate">{e.path}</code>
                <span>{e.duration_ms?.toFixed(0)}ms</span>
                <span className="text-red-500">{new Date(e.created_at).toLocaleTimeString()}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Client counts */}
      {clients?.length > 0 && (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <h3 className="font-semibold mb-3 text-gray-900 dark:text-white">Client Data Counts</h3>
          <table className="w-full text-sm">
            <thead className="text-gray-500 border-b border-gray-200 dark:border-gray-700">
              <tr><th className="text-left py-1">Client</th><th className="text-right">Invoices</th><th className="text-right">Receipts</th><th className="text-right">Journals</th><th className="text-right">Parties</th></tr>
            </thead>
            <tbody>
              {clients.map((c, i) => (
                <tr key={i} className="border-b border-gray-100 dark:border-gray-800">
                  <td className="py-1">{c.name}</td>
                  <td className="text-right">{c.invoices}</td>
                  <td className="text-right">{c.receipts}</td>
                  <td className="text-right">{c.journals}</td>
                  <td className="text-right">{c.parties}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, sub, warn }) {
  return (
    <div className={`rounded-lg border p-3 ${warn
      ? 'bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-800'
      : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700'}`}
    >
      <div className="text-xs text-gray-500 dark:text-gray-400">{label}</div>
      <div className={`text-xl font-bold ${warn ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-white'}`}>{value}</div>
      {sub && <div className="text-[10px] text-gray-500 mt-0.5">{sub}</div>}
    </div>
  );
}

/* ------------------------------------------------------------
 *  CLIENTS TAB
 * ------------------------------------------------------------ */
function ClientsTab() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await platformAPI.listClients();
      setClients(r.data.clients || []);
    } catch {
      toast.error('Failed to load clients');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const startReadonly = async (companyId, name) => {
    if (!window.confirm(`Enter read-only view for "${name}"?\n\nAll write actions will be disabled while this session is active.`)) return;
    try {
      await platformAPI.startReadonly(companyId);
      toast.success(`Now viewing: ${name} (read-only)`);
      // Reload the page so the banner + guarded UI kick in everywhere
      window.location.href = '/';
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to start session');
    }
  };

  return (
    <div className="space-y-4" data-testid="clients-tab">
      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-600 dark:text-gray-400">
          All Transsolution clients (tenant companies). {clients.length} total.
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="flex items-center gap-2 px-3 py-1.5 text-sm bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50"
          data-testid="clients-refresh"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        {clients.map(c => (
          <div
            key={c.company_id}
            className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4"
            data-testid={`client-card-${c.company_id}`}
          >
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white">{c.name}</h3>
                <code className="text-[11px] text-gray-500">{c.company_id}</code>
              </div>
              <button
                onClick={() => startReadonly(c.company_id, c.name)}
                data-testid={`view-client-${c.company_id}`}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-amber-500 text-white rounded hover:bg-amber-600"
                title="Enter read-only view of this client's data"
              >
                <Eye className="w-3.5 h-3.5" /> View (Read-only)
              </button>
            </div>
            <div className="grid grid-cols-4 gap-2 text-center text-xs">
              <div><div className="font-bold text-gray-900 dark:text-white">{c.stats?.invoices}</div><div className="text-gray-500">Invoices</div></div>
              <div><div className="font-bold text-gray-900 dark:text-white">{c.stats?.journals}</div><div className="text-gray-500">Journals</div></div>
              <div><div className="font-bold text-gray-900 dark:text-white">{c.stats?.users}</div><div className="text-gray-500">Users</div></div>
              <div><div className="font-bold text-gray-900 dark:text-white">{c.stats?.parties}</div><div className="text-gray-500">Parties</div></div>
            </div>
          </div>
        ))}
        {!loading && clients.length === 0 && (
          <div className="text-sm text-gray-500">No clients registered.</div>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------
 *  FIX HISTORY TAB — audit log of auto-fix operations
 * ------------------------------------------------------------ */
function FixHistoryTab() {
  const [ops, setOps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await platformAPI.fixOperations();
      setOps(r.data.operations || []);
    } catch {
      toast.error('Failed to load fix history');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (opId) => {
    try {
      const r = await platformAPI.fixOperation(opId);
      setSelected(r.data);
    } catch {
      toast.error('Failed to load operation details');
    }
  };

  const revert = async () => {
    if (!selected) return;
    if (!window.confirm(
      `Revert operation ${selected.operation_id}?\n\n` +
      `This will restore ${selected.plan_count} record(s) to their BEFORE state ` +
      `using the snapshot captured at apply time. This is a one-way action — ` +
      `reverted operations cannot be re-applied (create a new fix run instead).`
    )) return;
    try {
      const r = await platformAPI.revertFixOperation(selected.operation_id);
      toast.success(`Reverted ${r.data.reverted} record(s)`);
      setSelected(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Revert failed');
    }
  };

  return (
    <div className="space-y-4" data-testid="fix-history-tab">
      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-600 dark:text-gray-400">
          All auto-fix operations ever applied. {ops.length} shown.
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="flex items-center gap-2 px-3 py-1.5 text-sm bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50"
          data-testid="fix-history-refresh"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      {ops.length === 0 ? (
        <div className="text-sm text-gray-500">No fix operations yet.</div>
      ) : (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="text-xs text-gray-500 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
              <tr>
                <th className="text-left p-2">Operation</th>
                <th className="text-left p-2">Category</th>
                <th className="text-left p-2">By</th>
                <th className="text-left p-2">Started</th>
                <th className="text-right p-2">Count</th>
                <th className="text-left p-2">Status</th>
                <th className="p-2"></th>
              </tr>
            </thead>
            <tbody>
              {ops.map(op => (
                <tr key={op.operation_id} className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-900/50">
                  <td className="p-2"><code className="text-xs">{op.operation_id}</code></td>
                  <td className="p-2"><code className="text-xs">{op.category}</code></td>
                  <td className="p-2">{op.started_by_name}</td>
                  <td className="p-2 text-xs text-gray-600">{new Date(op.started_at).toLocaleString()}</td>
                  <td className="p-2 text-right">{op.plan_count}</td>
                  <td className="p-2">
                    <span className={`px-2 py-0.5 text-[10px] font-bold rounded ${
                      op.reverted ? 'bg-orange-100 text-orange-700' :
                      op.status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
                      op.status === 'failed' ? 'bg-red-100 text-red-700' :
                      'bg-amber-100 text-amber-700'
                    }`}>
                      {op.reverted ? 'reverted' : op.status}
                    </span>
                  </td>
                  <td className="p-2">
                    <button
                      onClick={() => openDetail(op.operation_id)}
                      className="text-xs text-purple-600 hover:underline"
                      data-testid={`fix-op-detail-${op.operation_id}`}
                    >
                      Details
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selected && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="fix-detail-modal">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[85vh] flex flex-col">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <div>
                <h3 className="font-bold text-gray-900 dark:text-white">Fix Operation Details</h3>
                <code className="text-xs text-gray-500">{selected.operation_id} · {selected.category}</code>
              </div>
              <button onClick={() => setSelected(null)} className="text-gray-400 hover:text-gray-700"><XCircle className="w-5 h-5" /></button>
            </div>
            <div className="p-4 overflow-y-auto flex-1">
              <div className="grid grid-cols-3 gap-3 mb-4 text-xs">
                <div><div className="text-gray-500">By</div><div className="font-semibold">{selected.started_by_name}</div></div>
                <div><div className="text-gray-500">Plan</div><div className="font-semibold">{selected.plan_count} records</div></div>
                <div><div className="text-gray-500">Applied</div><div className="font-semibold text-emerald-600">{selected.outcome?.success || 0}</div></div>
              </div>
              <div className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">Before/After Snapshots</div>
              <pre className="text-[11px] bg-gray-50 dark:bg-gray-900 p-3 rounded overflow-x-auto max-h-80">
                {JSON.stringify((selected.plan || []).slice(0, 20).map(p => ({
                  id: p.advance_id || p.entry_id || p.receipt_id || p.invoice_id,
                  action: p.action,
                  before: p.before,
                  after: p.after,
                })), null, 2)}
              </pre>
              {(selected.plan || []).length > 20 && (
                <div className="text-xs text-gray-500 mt-2">Showing first 20 of {selected.plan.length}</div>
              )}
              {selected.reverted && (
                <div className="mt-3 p-2 rounded bg-orange-50 dark:bg-orange-950/30 border border-orange-200 dark:border-orange-800 text-xs text-orange-900 dark:text-orange-200">
                  <strong>Reverted</strong> on {new Date(selected.reverted_at).toLocaleString()} by {selected.reverted_by_name} — restored {selected.reverted_count} record(s).
                </div>
              )}
            </div>
            {selected.status === 'completed' && !selected.reverted && (
              <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-end">
                <button
                  onClick={revert}
                  data-testid="fix-op-revert"
                  className="flex items-center gap-2 px-4 py-1.5 text-sm font-bold bg-orange-600 text-white rounded hover:bg-orange-700"
                >
                  <RefreshCw className="w-4 h-4" /> Revert This Operation
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------
 *  ISSUES TRIAGE TAB — SuperAdmin-only bug tracker
 * ------------------------------------------------------------ */
function IssuesTab() {
  const [data, setData] = useState({ issues: [], counts_by_status: {} });
  const [filter, setFilter] = useState({ status: '', module: '', priority: '' });
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(
        Object.entries(filter).filter(([_, v]) => v)
      );
      const r = await platformAPI.listIssues(params);
      setData(r.data);
    } catch {
      toast.error('Failed to load issues');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (issueId, status) => {
    try {
      await platformAPI.updateIssue(issueId, { status });
      toast.success(`Marked ${status}`);
      load();
      if (selected?.issue_id === issueId) setSelected({ ...selected, status });
    } catch {
      toast.error('Update failed');
    }
  };

  const STATUS_COLORS = {
    open: 'bg-red-100 text-red-700',
    in_progress: 'bg-amber-100 text-amber-800',
    resolved: 'bg-emerald-100 text-emerald-700',
    closed: 'bg-gray-200 text-gray-700',
    wont_fix: 'bg-gray-100 text-gray-500',
  };
  const PRIORITY_COLORS = {
    low: 'text-gray-600', medium: 'text-blue-600',
    high: 'text-amber-600', critical: 'text-red-600 font-bold',
  };

  return (
    <div className="space-y-4" data-testid="issues-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2">
          {['open', 'in_progress', 'resolved', 'closed', 'wont_fix'].map(s => (
            <button
              key={s}
              onClick={() => setFilter({ ...filter, status: filter.status === s ? '' : s })}
              data-testid={`issue-filter-${s}`}
              className={`px-2.5 py-1 text-xs font-medium rounded ${
                filter.status === s
                  ? 'bg-purple-600 text-white'
                  : `${STATUS_COLORS[s]} hover:opacity-80`
              }`}
            >
              {s.replace('_', ' ')} ({data.counts_by_status?.[s] || 0})
            </button>
          ))}
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="flex items-center gap-2 px-3 py-1.5 text-sm bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50"
          data-testid="issues-refresh"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      {data.issues.length === 0 ? (
        <div className="text-sm text-gray-500 p-6 text-center bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
          No issues match this filter.
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="text-xs text-gray-500 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
              <tr>
                <th className="text-left p-2">Title</th>
                <th className="text-left p-2">Module</th>
                <th className="text-left p-2">Priority</th>
                <th className="text-left p-2">Reporter</th>
                <th className="text-left p-2">Status</th>
                <th className="text-left p-2">Reported</th>
                <th className="p-2"></th>
              </tr>
            </thead>
            <tbody>
              {data.issues.map(i => (
                <tr key={i.issue_id} className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-900/50">
                  <td className="p-2 max-w-md truncate" title={i.title}>{i.title}</td>
                  <td className="p-2"><code className="text-xs text-purple-700 dark:text-purple-300">{i.module}</code></td>
                  <td className={`p-2 text-xs ${PRIORITY_COLORS[i.priority] || ''}`}>{i.priority}</td>
                  <td className="p-2 text-xs">{i.reporter_name}</td>
                  <td className="p-2">
                    <span className={`px-2 py-0.5 text-[10px] rounded ${STATUS_COLORS[i.status] || 'bg-gray-100'}`}>
                      {i.status}
                    </span>
                  </td>
                  <td className="p-2 text-xs text-gray-500">{new Date(i.created_at).toLocaleString()}</td>
                  <td className="p-2">
                    <button
                      onClick={() => setSelected(i)}
                      data-testid={`issue-open-${i.issue_id}`}
                      className="text-xs text-purple-600 hover:underline"
                    >
                      Open
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selected && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="issue-detail-modal">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[85vh] flex flex-col">
            <div className="flex items-start justify-between p-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <code className="text-xs text-gray-500">{selected.issue_id}</code>
                  <span className={`px-2 py-0.5 text-[10px] rounded ${STATUS_COLORS[selected.status]}`}>{selected.status}</span>
                  <span className={`text-xs ${PRIORITY_COLORS[selected.priority]}`}>{selected.priority}</span>
                  <code className="text-xs text-purple-700 dark:text-purple-300">{selected.module}</code>
                </div>
                <h3 className="font-bold text-gray-900 dark:text-white">{selected.title}</h3>
                <div className="text-xs text-gray-500 mt-1">
                  By {selected.reporter_name} ({selected.reporter_role}) · {new Date(selected.created_at).toLocaleString()}
                </div>
              </div>
              <button onClick={() => setSelected(null)} className="text-gray-400 hover:text-gray-700"><XCircle className="w-5 h-5" /></button>
            </div>
            <div className="p-4 overflow-y-auto flex-1 space-y-3">
              <div>
                <div className="text-xs font-medium text-gray-500 mb-1">Description</div>
                <div className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap">{selected.description}</div>
              </div>
              {selected.screenshot_url && (
                <div>
                  <div className="text-xs font-medium text-gray-500 mb-1">Screenshot</div>
                  <a href={selected.screenshot_url} target="_blank" rel="noopener noreferrer">
                    <img src={selected.screenshot_url} alt="issue screenshot" className="max-h-64 border border-gray-200 dark:border-gray-700 rounded" />
                  </a>
                </div>
              )}
              <div>
                <div className="text-xs font-medium text-gray-500 mb-1">History</div>
                <div className="space-y-1 text-xs">
                  {(selected.history || []).map((h, i) => (
                    <div key={i} className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                      <span>{new Date(h.at).toLocaleString()}</span>
                      <span className="font-medium">{h.by}</span>
                      <code>{h.action}</code>
                      {h.changes?.length > 0 && <span className="text-amber-600">{h.changes.join(', ')}</span>}
                      {h.note && <span>— {h.note}</span>}
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex flex-wrap justify-end gap-2">
              {selected.status !== 'in_progress' && (
                <button onClick={() => updateStatus(selected.issue_id, 'in_progress')} data-testid="issue-mark-progress" className="px-3 py-1.5 text-xs bg-amber-500 text-white rounded hover:bg-amber-600">Mark In Progress</button>
              )}
              {selected.status !== 'resolved' && (
                <button onClick={() => updateStatus(selected.issue_id, 'resolved')} data-testid="issue-mark-resolved" className="px-3 py-1.5 text-xs bg-emerald-600 text-white rounded hover:bg-emerald-700">Mark Resolved</button>
              )}
              {selected.status !== 'closed' && (
                <button onClick={() => updateStatus(selected.issue_id, 'closed')} data-testid="issue-mark-closed" className="px-3 py-1.5 text-xs bg-gray-600 text-white rounded hover:bg-gray-700">Close</button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
